import { SURF_DOC_SCHEMA_VERSION, SURF_DOC_TYPE, SURF_PLUGIN_ID, deckToPayloadJSON, emptyDeck, parseDeckPayload, renameDeck, duplicateDeck, } from "../model/surfDeck.js";
import { surfGraphQLFetch } from "../graphql/httpClient.js";
import { SURF_GRAPHQL } from "../graphql/queries.js";
import { IndexedDbSurfStore, MemorySurfStore, reconcileDeckRecords, snapshotFromRecords, } from "./surfStore.js";
import { COLD_SYNC_SINCE, } from "./types.js";
import { parseAccountKeyFromIdToken, readWatermark, writeWatermark, } from "./watermark.js";
const PUSH_DEBOUNCE_MS = 1500;
const MAX_PAGES_DEFAULT = 20;
function pluginRowToRecord(row) {
    if (row.docType !== SURF_DOC_TYPE || row.pluginId !== SURF_PLUGIN_ID)
        return null;
    const deck = parseDeckPayload(row.payloadJSON);
    if (!deck)
        return null;
    return {
        deck,
        documentId: row.documentId,
        version: row.version ?? undefined,
        updatedAt: row.updatedAt,
        deleted: row.deleted ?? false,
    };
}
function createMemoryStorage() {
    const map = new Map();
    return {
        getItem: (k) => map.get(k) ?? null,
        setItem: (k, v) => {
            map.set(k, v);
        },
        removeItem: (k) => {
            map.delete(k);
        },
        clear: () => map.clear(),
        key: () => null,
        length: 0,
    };
}
export class SurfSyncEngine {
    cfg;
    status = "idle";
    store;
    storage;
    pushTimers = new Map();
    pendingDecks = new Map();
    accountKey = null;
    started = false;
    constructor(cfg) {
        this.cfg = cfg;
        this.store =
            cfg.store ??
                (typeof indexedDB !== "undefined" ? new IndexedDbSurfStore() : new MemorySurfStore());
        this.storage = cfg.storage ?? createMemoryStorage();
    }
    getStatus() {
        return this.status;
    }
    async start() {
        if (this.started)
            return;
        this.started = true;
        const cached = await this.store.read();
        if (cached.decks.length > 0)
            this.cfg.onSnapshot?.(cached);
        await this.refreshAccountKey();
        await this.pullDelta();
    }
    async stop() {
        this.started = false;
        for (const timer of this.pushTimers.values())
            clearTimeout(timer);
        this.pushTimers.clear();
        this.setStatus("idle");
    }
    async pullOnce() {
        await this.pullDelta();
        return this.store.read();
    }
    getSnapshot() {
        return this.store.read();
    }
    async createDeck(title, projectId) {
        const deck = emptyDeck(title);
        const row = await this.putDeckRemote(deck, projectId);
        await this.upsertLocalRecord(row);
        return row;
    }
    async saveDeck(deck, expectedVersion) {
        const row = await this.updateDeckRemote(deck, expectedVersion);
        await this.upsertLocalRecord(row);
        return row;
    }
    scheduleSaveDeck(deck, expectedVersion) {
        this.pendingDecks.set(deck.id, deck);
        const existing = this.pushTimers.get(deck.id);
        if (existing)
            clearTimeout(existing);
        const timer = setTimeout(() => {
            void this.flushDeck(deck.id, expectedVersion);
        }, PUSH_DEBOUNCE_MS);
        this.pushTimers.set(deck.id, timer);
    }
    async renameDeck(documentId, title) {
        const snapshot = await this.store.read();
        const current = snapshot.decks.find((d) => d.documentId === documentId);
        if (!current)
            throw new Error("Deck non trovato");
        const nextDeck = renameDeck(current.deck, title);
        return this.saveDeck(nextDeck, current.version);
    }
    async duplicateDeck(documentId) {
        const snapshot = await this.store.read();
        const current = snapshot.decks.find((d) => d.documentId === documentId);
        if (!current)
            throw new Error("Deck non trovato");
        const copy = duplicateDeck(current.deck);
        const row = await this.putDeckRemote(copy);
        await this.upsertLocalRecord(row);
        return row;
    }
    async deleteDeck(documentId) {
        const idToken = await this.cfg.getIdToken();
        await surfGraphQLFetch({
            endpoint: this.cfg.endpoint,
            idToken,
            query: SURF_GRAPHQL.deletePluginDocument,
            variables: { documentId },
            resultKey: "deletePluginDocument",
        });
        const snapshot = await this.store.read();
        const next = snapshot.decks.filter((d) => d.documentId !== documentId);
        const updated = snapshotFromRecords(next, snapshot.lastSyncedAt);
        await this.store.write(updated);
        this.cfg.onSnapshot?.(updated);
    }
    async flushDeck(deckId, expectedVersion) {
        const deck = this.pendingDecks.get(deckId);
        if (!deck)
            return;
        this.pendingDecks.delete(deckId);
        this.pushTimers.delete(deckId);
        await this.saveDeck(deck, expectedVersion);
    }
    async putDeckRemote(deck, projectId) {
        const idToken = await this.cfg.getIdToken();
        const input = {
            documentId: deck.id,
            pluginId: SURF_PLUGIN_ID,
            docType: SURF_DOC_TYPE,
            docSchemaVersion: SURF_DOC_SCHEMA_VERSION,
            title: deck.title,
            state: "active",
            payloadJSON: deckToPayloadJSON(deck),
        };
        if (projectId)
            input.pulseFolderId = projectId;
        const row = await surfGraphQLFetch({
            endpoint: this.cfg.endpoint,
            idToken,
            query: SURF_GRAPHQL.putPluginDocument,
            variables: { input },
            resultKey: "putPluginDocument",
        });
        const record = pluginRowToRecord(row);
        if (!record)
            throw new Error("Risposta putPluginDocument non valida");
        return record;
    }
    async updateDeckRemote(deck, expectedVersion) {
        const idToken = await this.cfg.getIdToken();
        const row = await surfGraphQLFetch({
            endpoint: this.cfg.endpoint,
            idToken,
            query: SURF_GRAPHQL.updatePluginDocument,
            variables: {
                documentId: deck.id,
                input: {
                    title: deck.title,
                    state: "active",
                    docSchemaVersion: SURF_DOC_SCHEMA_VERSION,
                    payloadJSON: deckToPayloadJSON(deck),
                    expectedVersion: expectedVersion ?? undefined,
                },
            },
            resultKey: "updatePluginDocument",
        });
        const record = pluginRowToRecord(row);
        if (!record)
            throw new Error("Risposta updatePluginDocument non valida");
        return record;
    }
    async upsertLocalRecord(record) {
        const snapshot = await this.store.read();
        const others = snapshot.decks.filter((d) => d.documentId !== record.documentId);
        const next = snapshotFromRecords([...others, record], snapshot.lastSyncedAt);
        await this.store.write(next);
        this.cfg.onSnapshot?.(next);
    }
    async refreshAccountKey() {
        const idToken = await this.cfg.getIdToken();
        this.accountKey = parseAccountKeyFromIdToken(idToken, this.cfg.decodeJwt);
    }
    setStatus(status) {
        this.status = status;
        this.cfg.onStatus?.(status);
    }
    async pullDelta() {
        if (!this.accountKey)
            await this.refreshAccountKey();
        this.setStatus("syncing");
        try {
            const idToken = await this.cfg.getIdToken();
            const since = (this.accountKey && readWatermark(this.storage, this.accountKey)) || COLD_SYNC_SINCE;
            const remoteRecords = [];
            let nextToken;
            let syncedAt = since;
            let pages = 0;
            const maxPages = this.cfg.maxPages ?? MAX_PAGES_DEFAULT;
            do {
                pages += 1;
                const page = await surfGraphQLFetch({
                    endpoint: this.cfg.endpoint,
                    idToken,
                    query: SURF_GRAPHQL.deltaSync,
                    variables: {
                        input: {
                            lastSyncAt: pages === 1 ? since : COLD_SYNC_SINCE,
                            nextToken,
                        },
                    },
                    resultKey: "deltaSync",
                });
                for (const row of page.pluginDocuments ?? []) {
                    const record = pluginRowToRecord(row);
                    if (record)
                        remoteRecords.push(record);
                }
                syncedAt = page.syncedAt || syncedAt;
                nextToken = page.nextToken ?? undefined;
            } while (nextToken && pages < maxPages);
            const local = await this.store.read();
            const merged = reconcileDeckRecords(local.decks, remoteRecords);
            const snapshot = snapshotFromRecords(merged, syncedAt);
            await this.store.write(snapshot);
            if (this.accountKey)
                writeWatermark(this.storage, this.accountKey, syncedAt);
            this.cfg.onSnapshot?.(snapshot);
            this.setStatus("idle");
        }
        catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            this.cfg.onError?.(message);
            this.setStatus("error");
            throw e;
        }
    }
}
/** Open deck without bumping contentUpdatedAt — for GATE F1 LWW check. */
export function openDeckForView(record) {
    return structuredClone(record.deck);
}
